import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { 
  ChevronLeft, FileText, DollarSign, Tag, User, Eye, Download, 
  Clock, AlertCircle, RefreshCw, Shield, Check, Upload, Edit3,
  UserCheck, ArrowRight, CheckCircle 
} from 'lucide-react';
import { useAuth } from '../App.jsx';
import CotizacionTramiteModal from '../components/CotizacionTramiteModal.jsx';
import FacturacionCardInfo from '../components/FacturacionCardInfo.jsx';
import RectificarTramiteModal from '../components/Tramites/RectificarTramiteModal.jsx';
import VisualizadorDocumentosModal from '../components/Tramites/VisualizadorDocumentosModal.jsx';

const API = import.meta.env.VITE_API_URL || 'https://cbrsancarlos.cl/api';

/* ─── Helpers ──────────────────────────────────────────────────────────── */
const ACTION_LABELS = {
  creacion:                     { label: 'Trámite Ingresado',                color: '#2563eb', bg: 'rgba(37, 99, 235, 0.1)' },
  cambio_estado:                { label: 'Cambio de Estado',                 color: '#7c3aed', bg: 'rgba(124, 58, 237, 0.1)' },
  asignacion:                   { label: 'Trámite Asignado',                 color: '#0284c7', bg: 'rgba(2, 132, 199, 0.1)' },
  cambio_estado_y_asignacion:   { label: 'Cambio de Estado y Asignación',    color: '#6366f1', bg: 'rgba(99, 102, 241, 0.1)' },
  envio_cotizacion:             { label: 'Cotización Emitida',               color: '#d97706', bg: 'rgba(217, 119, 6, 0.1)' },
  confirmacion_pago:            { label: 'Pago Confirmado',                  color: '#059669', bg: 'rgba(5, 150, 105, 0.1)' },
  rechazo_pago:                 { label: 'Pago Rechazado',                   color: '#dc2626', bg: 'rgba(220, 38, 38, 0.1)' },
  devolucion:                   { label: 'Devolución de Fondos',             color: '#d97706', bg: 'rgba(217, 119, 6, 0.1)' },
  adjunto_documento:            { label: 'Documento Adjuntado',              color: '#059669', bg: 'rgba(5, 150, 105, 0.1)' },
  correo_enviado:               { label: 'Correo Enviado al Ciudadano',      color: '#d97706', bg: 'rgba(217, 119, 6, 0.1)' },
  respuesta_ciudadano:          { label: 'Respuesta del Ciudadano',          color: '#0891b2', bg: 'rgba(8, 145, 178, 0.1)' },
  rectificacion_tipo:           { label: 'Rectificación de Acto / Inscripción', color: '#ea580c', bg: 'rgba(234, 88, 12, 0.1)' },
  marcado_exento_repertorio:    { label: 'Marcado Exento de Repertorio',     color: '#4f46e5', bg: 'rgba(79, 70, 229, 0.1)' },
  desmarcado_exento_repertorio: { label: 'Desmarcado de Exención',           color: '#64748b', bg: 'rgba(100, 116, 139, 0.1)' },
  reapertura_staff:             { label: 'Trámite Reabierto',                color: '#ea580c', bg: 'rgba(234, 88, 12, 0.1)' },
  entrega_documentos:           { label: 'Documentos Entregados',           color: '#059669', bg: 'rgba(5, 150, 105, 0.1)' },
  rechazo:                      { label: 'Trámite Rechazado',                color: '#dc2626', bg: 'rgba(220, 38, 38, 0.1)' },
  actualizacion:                { label: 'Actualización de Datos',           color: '#64748b', bg: 'rgba(100, 116, 139, 0.1)' },
};

function formatWorkerName(worker, payload = {}) {
  if (payload?.usuario || payload?.funcionario) {
    const u = String(payload.usuario || payload.funcionario).trim();
    if (u.toLowerCase().includes('admin')) return 'Sistema';
    return u;
  }
  if (!worker) return 'Sistema';
  const name = (typeof worker === 'string' ? worker : (worker.name || '')).trim();
  const lower = name.toLowerCase();
  const role = (worker.role || '').toLowerCase();
  if (!name || lower.includes('admin') || role === 'admin' || role === 'super_admin' || worker.id === 1) {
    return 'Sistema';
  }
  return name;
}

/* ─── Sub-componente: Panel de Historial ───────────────────────────────── */
function HistorialPanel({ steps = [] }) {
  if (!steps.length) {
    return <p style={{ color: 'var(--gray-500)', fontSize: '0.85rem' }}>Sin historial registrado.</p>;
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
      {steps.map(step => {
        const meta = ACTION_LABELS[step.action] || ACTION_LABELS.actualizacion;
        const esCiudadano = step.action === 'respuesta_ciudadano';
        return (
          <div key={step.id} style={{ display: 'flex', gap: '0.75rem', alignItems: 'flex-start' }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
              background: meta.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
              border: `1px solid ${meta.color}33`
            }}>
              {esCiudadano ? <User size={15} color={meta.color} /> : <Shield size={15} color={meta.color} />}
            </div>
            <div style={{
              flex: 1, background: 'rgba(255,255,255,0.02)', borderRadius: 10, padding: '0.6rem 0.9rem',
              border: `1px solid ${meta.color}22`,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                <span style={{ fontWeight: 700, fontSize: '0.78rem', color: meta.color }}>
                  {meta.label}
                </span>
                <span style={{ fontSize: '0.72rem', color: 'var(--gray-500)' }}>
                  {new Date(step.created_at).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' })}
                  {!esCiudadano && (
                    <span style={{ marginLeft: 6 }}>
                      · {step.display_worker_name || formatWorkerName(step.worker, step.payload)}
                    </span>
                  )}
                </span>
              </div>
              {step.payload?.asunto && (
                <div style={{ fontSize: '0.82rem', marginBottom: 2, color: 'var(--gray-200)' }}>
                  <strong>Asunto:</strong> {step.payload.asunto}
                </div>
              )}
              {(step.payload?.mensaje || step.payload?.comentario) && (
                <div style={{ fontSize: '0.82rem', color: 'var(--gray-300)', whiteSpace: 'pre-wrap' }}>
                  {step.payload.mensaje || step.payload.comentario}
                </div>
              )}
              {step.payload?.nota && (
                <div style={{ fontSize: '0.78rem', color: 'var(--gray-500)', fontStyle: 'italic' }}>
                  {step.payload.nota}
                </div>
              )}

              {/* Detalle de Asignación */}
              {(step.action === 'asignacion' || step.action === 'cambio_estado_y_asignacion' || step.payload?.changes?.asignado_a) && (
                <div style={{ fontSize: '0.8rem', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--gray-200)' }}>
                  <UserCheck size={13} style={{ color: '#0284c7', flexShrink: 0 }} />
                  <span>
                    <strong>Asignado a:</strong>{' '}
                    <strong style={{ color: '#38bdf8' }}>
                      {step.payload?.changes?.asignado_a?.new || step.payload?.funcionario || 'Funcionario'}
                    </strong>
                    {step.payload?.changes?.asignado_a?.old && step.payload?.changes?.asignado_a?.old !== 'Sin asignar' && (
                      <span style={{ color: 'var(--gray-400)', fontSize: '0.74rem', marginLeft: 6 }}>
                        (anteriormente: {step.payload.changes.asignado_a.old})
                      </span>
                    )}
                  </span>
                </div>
              )}

              {/* Detalle de Cambio de Estado */}
              {(step.action === 'cambio_estado' || step.action === 'cambio_estado_y_asignacion' || step.payload?.changes?.estado) && (
                <div style={{ fontSize: '0.8rem', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--gray-200)' }}>
                  <ArrowRight size={12} style={{ color: '#a855f7', flexShrink: 0 }} />
                  <span>
                    {step.payload?.changes?.estado ? (
                      <>
                        <span style={{ color: 'var(--gray-400)' }}>{step.payload.changes.estado.old}</span>
                        <span style={{ margin: '0 4px', color: '#a855f7', fontWeight: 700 }}>➔</span>
                        <strong style={{ color: '#c084fc' }}>{step.payload.changes.estado.new}</strong>
                      </>
                    ) : (step.old_state && step.new_state && step.old_state !== step.new_state) ? (
                      <>
                        <span style={{ color: 'var(--gray-400)' }}>{step.old_state}</span>
                        <span style={{ margin: '0 4px', color: '#a855f7', fontWeight: 700 }}>➔</span>
                        <strong style={{ color: '#c084fc' }}>{step.new_state}</strong>
                      </>
                    ) : (
                      <strong>{step.new_state || 'Estado actualizado'}</strong>
                    )}
                  </span>
                </div>
              )}

              {/* Detalle de Cotización */}
              {step.action === 'envio_cotizacion' && (
                <div style={{ fontSize: '0.8rem', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, color: 'var(--gray-200)' }}>
                  <DollarSign size={13} style={{ color: '#f59e0b', flexShrink: 0 }} />
                  <span>
                    <strong>Total Cotizado:</strong> ${Number(step.payload?.total || 0).toLocaleString('es-CL')}
                    {step.payload?.caja && (
                      <span style={{ marginLeft: 6, color: 'var(--gray-400)', fontSize: '0.75rem' }}>
                        · Caja: {step.payload.caja}
                      </span>
                    )}
                  </span>
                </div>
              )}

              {/* Detalle de Confirmación de Pago */}
              {step.action === 'confirmacion_pago' && (
                <div style={{ fontSize: '0.8rem', marginTop: 4, display: 'flex', alignItems: 'center', gap: 6, color: '#4ade80' }}>
                  <CheckCircle size={13} style={{ flexShrink: 0 }} />
                  <span>
                    <strong>Monto confirmado:</strong> ${Number(step.payload?.total || step.payload?.monto || 0).toLocaleString('es-CL')}
                  </span>
                </div>
              )}

              {/* Otros campos modificados */}
              {step.payload?.changes && Object.keys(step.payload.changes).filter(k => !['estado', 'estado_id', 'asignado_a'].includes(k)).length > 0 && (
                <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 3, fontSize: '0.78rem' }}>
                  {Object.entries(step.payload.changes)
                    .filter(([k]) => !['estado', 'estado_id', 'asignado_a'].includes(k))
                    .map(([field, val]) => (
                      <div key={field} style={{ background: 'rgba(255,255,255,0.03)', padding: '3px 8px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.08)', color: 'var(--gray-300)' }}>
                        <strong style={{ textTransform: 'capitalize' }}>{field.replace(/_/g, ' ')}:</strong>{' '}
                        <span style={{ color: 'var(--gray-500)', textDecoration: 'line-through' }}>{String(val?.old ?? '—')}</span>
                        <span style={{ margin: '0 4px', color: 'var(--gray-400)' }}>➔</span>
                        <strong style={{ color: 'var(--gray-100)' }}>{String(val?.new ?? '—')}</strong>
                      </div>
                    ))}
                </div>
              )}

              {/* Comentarios de auditoría adicionales */}
              {step.comments && !step.payload?.mensaje && !step.payload?.comentario && step.action !== 'rectificacion_tipo' && (
                <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)', marginTop: 4, fontStyle: 'italic' }}>
                  {step.comments}
                </div>
              )}
              {step.action === 'rectificacion_tipo' && (
                <div style={{ marginTop: 6, background: 'rgba(255,255,255,0.03)', padding: '8px 10px', borderRadius: 8, border: '1px solid rgba(234, 88, 12, 0.3)', fontSize: '0.8rem' }}>
                  <div style={{ color: '#fb923c', fontWeight: 700, marginBottom: 3, display: 'flex', alignItems: 'center', gap: 4 }}>
                    <Edit3 size={12} /> Modificación del Acto / Inscripción:
                  </div>
                  <div style={{ color: 'var(--gray-400)', textDecoration: 'line-through', fontSize: '0.76rem' }}>
                    Anterior: {step.payload?.tipo_anterior || step.old_state} ({step.payload?.area_anterior || 'General'})
                  </div>
                  <div style={{ color: '#4ade80', fontWeight: 600, fontSize: '0.8rem', marginTop: 2 }}>
                    Rectificado a: {step.payload?.tipo_nuevo || step.new_state} ({step.payload?.area_nueva || 'General'})
                  </div>
                  {step.payload?.motivo && (
                    <div style={{ color: 'var(--gray-300)', fontSize: '0.76rem', marginTop: 4, background: 'rgba(234, 88, 12, 0.08)', padding: '4px 6px', borderRadius: 4 }}>
                      <strong>Motivo:</strong> {step.payload.motivo}
                    </div>
                  )}
                </div>
              )}
              {step.payload?.archivos?.length > 0 && (
                <ul style={{ margin: '4px 0 0', paddingLeft: '1.2rem', fontSize: '0.78rem', color: 'var(--gray-300)' }}>
                  {step.payload.archivos.map((a, i) => <li key={i}>{a}</li>)}
                </ul>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Sub-componente: Modal Correo ─────────────────────────────────────── */
function ModalCorreo({ tramite, onClose, onSent }) {
  const [asunto, setAsunto]   = useState('');
  const [mensaje, setMensaje] = useState('');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    if (!asunto.trim() || !mensaje.trim()) {
      toast.error('El asunto y el mensaje son obligatorios.');
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/v1/tramites/${tramite.id}/correo`, { asunto, mensaje });
      toast.success('Correo enviado al ciudadano y registrado en el historial.');
      onSent();
      onClose();
    } catch {
      toast.error('Error al enviar el correo.');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 520 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2 style={{ fontSize: '1.1rem', display: 'flex', alignItems: 'center', gap: 8 }}>
             <Tag size={18} color="var(--copper)" /> Enviar Correo al Ciudadano
          </h2>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div style={{ padding: '1rem' }}>
          <p style={{ fontSize: '0.83rem', color: 'var(--gray-500)', marginBottom: '1rem' }}>
            Para: <strong>{tramite.nombre_solicitante}</strong> ({tramite.correo})
            &nbsp;·&nbsp;Trámite <strong>{tramite.numero_seguimiento}</strong>
          </p>
          <div className="form-group">
            <label>Asunto</label>
            <input value={asunto} onChange={e => setAsunto(e.target.value)} placeholder="Ej: Información adicional requerida" />
          </div>
          <div className="form-group">
            <label>Mensaje</label>
            <textarea rows={5} value={mensaje} onChange={e => setMensaje(e.target.value)}
              placeholder="Escribe aquí el mensaje para el ciudadano..." />
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.5rem' }}>
            <button className="btn btn-secondary" onClick={onClose} disabled={sending}>Cancelar</button>
            <button className="btn" style={{ background: 'var(--copper)', color: 'white' }} onClick={handleSend} disabled={sending}>
              {sending ? <span className="spinner" /> : 'Enviar Mensaje'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function TramiteDetalle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, hasRole } = useAuth();
  const [tramite, setTramite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reopening, setReopening] = useState(false);
  const [reopenModal, setReopenModal] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [showCotizacionModal, setShowCotizacionModal] = useState(false);
  const [confirmingPago, setConfirmingPago] = useState(false);
  const [modalCorreo, setModalCorreo] = useState(false);
  const [modalRectificar, setModalRectificar] = useState(false);
  const [docModal, setDocModal] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/v1/tramites/${id}`);
      setTramite(res.data);
    } catch {
      toast.error('Error al cargar detalle del trámite.');
      navigate('/funcionario');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { cargar(); }, [cargar]);

  const handleReopen = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      toast.error('Debe adjuntar el nuevo documento para reabrir.');
      return;
    }

    setReopening(true);
    const formData = new FormData();
    formData.append('documento', selectedFile);
    formData.append('audit_payload', JSON.stringify({ reason: 'Reapertura por usuario' }));

    try {
      await axios.post(`${API}/v1/tramites/${id}/reabrir-staff`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Trámite reabierto con éxito.');
      setReopenModal(false);
      cargar();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Error al reabrir trámite.');
    } finally {
      setReopening(false);
    }
  };

  const handleConfirmarPago = async () => {
    const result = await Swal.fire({
      title: '¿Confirmar Pago?',
      text: '¿Confirmar que el pago ha sido recibido y validado?',
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: 'var(--primary)',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Sí, confirmar',
      cancelButtonText: 'Cancelar'
    });

    if (!result.isConfirmed) return;
    setConfirmingPago(true);
    try {
      await axios.post(`${API}/v1/tramites/${id}/confirmar-pago`);
      toast.success('Pago confirmado. Trámite actualizado.');
      cargar();
    } catch (err) {
      toast.error('Error al confirmar pago.');
    } finally {
      setConfirmingPago(false);
    }
  };

  if (loading) return <div style={{ textAlign: 'center', padding: '5rem' }}><span className="spinner spinner-lg" /></div>;
  if (!tramite) return null;

  const isRejected = tramite.estado?.nombre === 'Rehusado';
  const isIngresado = tramite.estado?.nombre === 'Ingresado';
  const isPendientePago = tramite.estado?.nombre === 'Pendiente Pago';
  const hasRepertorio = tramite.repertorios && tramite.repertorios.length > 0;

  const canReopen = isRejected && (hasRole('boss') || hasRole('super_admin') || tramite.asignado_a === user?.id);
  const canCotizar = (isIngresado || tramite.estado?.nombre === 'Procesando') && (hasRole('boss') || hasRole('super_admin') || hasRole('admin') || hasRole('staff'));
  const isCaja = user?.is_caja;

  return (
    <div className="container animate-fade-in" style={{ padding: '2rem clamp(16px,4vw,48px) 4rem' }}>
      <button onClick={() => navigate(-1)} className="btn btn-secondary btn-sm" style={{ marginBottom: '1.5rem' }}>
        <ChevronLeft size={16} /> Volver
      </button>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.5fr) minmax(0, 1fr)', gap: '2rem', alignItems: 'start' }}>
        
        {/* Columna Izquierda: Información y Documentos */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          
          <div className="glass-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ fontSize: '1.4rem', color: 'var(--copper-light)' }}>
                <FileText size={20} style={{ verticalAlign: 'middle', marginRight: 8 }} />
                Trámite {tramite.numero_seguimiento}
              </h2>
              <span className={`badge ${
                tramite.estado?.nombre === 'Completado' ? 'badge-success' : 
                tramite.estado?.nombre === 'Rehusado' ? 'badge-error' : 
                tramite.estado?.nombre === 'Pagado' ? 'badge-success' : 'badge-warning'
              }`}>
                {tramite.estado?.nombre}
              </span>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.5rem' }}>
              {canCotizar && (
                <button
                  onClick={() => setShowCotizacionModal(true)}
                  className="btn btn-sm"
                  style={{ background: '#059669', color: 'white' }}
                  disabled={!hasRepertorio}
                  title={!hasRepertorio ? 'Debe asignar repertorio antes de cotizar' : ''}
                >
                  <DollarSign size={14} /> {tramite.cotizacion ? 'Editar Cotización' : 'Cotizar Trámite'}
                </button>
              )}

              <button
                onClick={() => setModalCorreo(true)}
                className="btn btn-sm btn-secondary"
                style={{ display: 'flex', alignItems: 'center', gap: 6 }}
              >
                <Tag size={14} /> Enviar Correo
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '1.5rem' }}>
              <div className="info-item">
                <label style={{ fontSize: '0.75rem', color: 'var(--gray-500)', display: 'block', textTransform: 'uppercase' }}>Solicitante</label>
                <div style={{ fontWeight: 600 }}>{tramite.nombre_solicitante}</div>
                <div style={{ fontSize: '0.85rem', color: 'var(--gray-400)' }}>{tramite.rut_solicitante}</div>
              </div>
              <div className="info-item">
                <label style={{ fontSize: '0.75rem', color: 'var(--gray-500)', display: 'block', textTransform: 'uppercase' }}>Contacto</label>
                <div style={{ fontSize: '0.85rem' }}>{tramite.correo}</div>
                <div style={{ fontSize: '0.85rem' }}>{tramite.telefono}</div>
              </div>
              <div className="info-item">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <label style={{ fontSize: '0.75rem', color: 'var(--gray-500)', display: 'block', textTransform: 'uppercase' }}>Tipo de Trámite</label>
                  <button
                    type="button"
                    onClick={() => setModalRectificar(true)}
                    title="Rectificar Tipo de Trámite ante llamado o error"
                    style={{
                      background: 'rgba(234, 88, 12, 0.12)',
                      color: '#fb923c',
                      border: '1px solid rgba(234, 88, 12, 0.3)',
                      borderRadius: '4px',
                      padding: '1px 7px',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 3
                    }}
                  >
                    <Edit3 size={11} /> Rectificar
                  </button>
                </div>
                <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>{tramite.tipo_inscripcion?.nombre}</div>
                <span className="badge badge-gray" style={{ marginTop: 4 }}>{tramite.area?.nombre}</span>
              </div>
              <div className="info-item">
                <label style={{ fontSize: '0.75rem', color: 'var(--gray-500)', display: 'block', textTransform: 'uppercase' }}>Responsable</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <User size={14} className="text-copper" />
                  <span style={{ fontSize: '0.9rem' }}>{tramite.assigned_to?.name || 'Por asignar'}</span>
                </div>
              </div>
            </div>

            {/* Ficha de Facturación / Boleta */}
            <FacturacionCardInfo data={tramite} isDark={true} style={{ marginTop: '1.5rem' }} />

            {tramite.observaciones && (
              <div style={{ marginTop: '2rem', padding: '1rem', background: 'rgba(0,0,0,0.2)', borderRadius: '8px' }}>
                <label style={{ fontSize: '0.75rem', color: 'var(--gray-500)', display: 'block', marginBottom: 4 }}>Observaciones del Solicitante</label>
                <p style={{ fontSize: '0.85rem', color: 'var(--gray-300)', margin: 0 }}>{tramite.observaciones}</p>
              </div>
            )}
          </div>

          <div className="glass-card">
            <h3 style={{ fontSize: '1.1rem', marginBottom: '1.2rem' }}>📎 Documentación Adjunta</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              {tramite.documentos?.map(doc => (
                <div key={doc.id} className="glass-card" style={{ padding: '0.75rem 1rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'rgba(255,255,255,0.03)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <div style={{ width: 32, height: 32, borderRadius: '6px', background: 'var(--midnight)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      📄
                    </div>
                    <div>
                      <div style={{ fontSize: '0.85rem', fontWeight: 600 }}>{doc.nombre_original}</div>
                      <div style={{ fontSize: '0.7rem', color: 'var(--gray-500)' }}>PDF / DOC</div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => setDocModal(doc)}
                      className="icon-btn"
                      title="Ver en visualizador"
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}
                    >
                      <Eye size={18} />
                    </button>
                    <a href={`${API}/api/v1/tramites/documentos/${doc.token_descarga}/descargar`} target="_blank" rel="noreferrer" className="icon-btn" title="Descargar">
                      <Download size={18} />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Columna Derecha: Timeline y Acciones */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
          
          <div className="glass-card">
            <h3 style={{ fontSize: '1.1rem', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: 8 }}>
              <Clock size={18} className="text-copper" /> Historial y Mensajes
            </h3>
            
            <HistorialPanel steps={tramite.workflow_steps || []} />
          </div>

          {canReopen && (
            <div className="glass-card" style={{ border: '1px solid var(--error-dim)', background: 'rgba(239, 68, 68, 0.05)' }}>
              <h4 style={{ color: 'var(--error)', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                <AlertCircle size={18} /> Acción Requerida
              </h4>
              <p style={{ fontSize: '0.85rem', color: 'var(--gray-400)', marginBottom: '1.2rem' }}>
                Este trámite fue rehusado. Si los errores han sido subsanados, puede reabrirlo adjuntando la nueva documentación.
              </p>
              <button onClick={() => setReopenModal(true)} className="btn" style={{ background: 'var(--error)', color: 'white', width: '100%' }}>
                <RefreshCw size={16} /> Reabrir Trámite
              </button>
            </div>
          )}

          {isPendientePago && isCaja && (
            <div className="glass-card" style={{ border: '1px solid var(--primary-dim)', background: 'rgba(26, 30, 56, 0.05)' }}>
              <h4 style={{ color: 'var(--primary)', marginBottom: '0.5rem', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Shield size={18} /> Validación de Pago (Caja)
              </h4>
              <p style={{ fontSize: '0.85rem', color: 'var(--gray-400)', marginBottom: '1.2rem' }}>
                Este trámite está pendiente de pago. Una vez que valide el comprobante, confirme aquí para avanzar el proceso.
              </p>
              <button
                onClick={handleConfirmarPago}
                className="btn btn-primary"
                style={{ width: '100%' }}
                disabled={confirmingPago}
              >
                {confirmingPago ? <span className="spinner" /> : <><Check size={16} /> Confirmar Pago Recibido</>}
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Modal de Cotización */}
      {showCotizacionModal && (
        <CotizacionTramiteModal
          tramite={tramite}
          onClose={() => setShowCotizacionModal(false)}
          onUpdate={cargar}
        />
      )}

      {/* Modal de Correo */}
      {modalCorreo && (
        <ModalCorreo
          tramite={tramite}
          onClose={() => setModalCorreo(false)}
          onSent={cargar}
        />
      )}

      {/* Modal de Reapertura */}
      {reopenModal && (
        <div className="modal-overlay" onClick={(e) => e.target === e.currentTarget && setReopenModal(false)}>
          <div className="modal-content" style={{ maxWidth: 450 }}>
            <div className="modal-header">
              <h3>Reabrir Trámite</h3>
              <button className="modal-close" onClick={() => setReopenModal(false)}>✕</button>
            </div>
            
            <form onSubmit={handleReopen} style={{ marginTop: '1rem' }}>
              <div className="alert alert-warning" style={{ marginBottom: '1.5rem', fontSize: '0.85rem' }}>
                <strong>⚠️ Importante:</strong> Para reabrir este trámite es obligatorio adjuntar el nuevo documento que corrige las observaciones del rechazo.
              </div>
              
              <div className="form-group">
                <label>Documento de Subsanación (PDF/Imagen) *</label>
                <div style={{ padding: '1.5rem', border: '2px dashed var(--gray-700)', borderRadius: '8px', textAlign: 'center' }}>
                  <input 
                    type="file" 
                    onChange={e => setSelectedFile(e.target.files[0])}
                    style={{ position: 'absolute', opacity: 0, width: 0 }}
                    id="reopen-file"
                  />
                  <label htmlFor="reopen-file" style={{ cursor: 'pointer' }}>
                    <Upload size={32} className="text-copper" style={{ marginBottom: 8 }} />
                    <div style={{ fontWeight: 600 }}>{selectedFile ? selectedFile.name : 'Seleccionar Archivo'}</div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>Haz clic para buscar en tu equipo</div>
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '2rem', justifyContent: 'flex-end' }}>
                <button type="button" className="btn btn-secondary" onClick={() => setReopenModal(false)}>Cancelar</button>
                <button type="submit" className="btn" style={{ background: 'var(--copper)', color: 'white' }} disabled={reopening}>
                  {reopening ? <span className="spinner" /> : 'Confirmar y Reabrir'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {modalRectificar && (
        <RectificarTramiteModal
          tramite={tramite}
          onClose={() => setModalRectificar(false)}
          onSuccess={(updated) => {
            setTramite(updated);
            cargar();
          }}
        />
      )}

      {/* Visualizador Bonito de Documentos */}
      {docModal && (
        <VisualizadorDocumentosModal
          isOpen={Boolean(docModal)}
          onClose={() => setDocModal(null)}
          tramite={tramite}
          initialDoc={docModal}
        />
      )}
    </div>
  );
}
